/**
 * Phase 8 T-15 §14 A–B — Wrangle-style onboarding shell.
 *
 * Full-screen dimmed backdrop over a muted shadow. Centred 1000 px container:
 *   - Left: 420 px form card  (`<Outlet />` renders the step content)
 *   - Right: 260 px sticky progress widget
 *
 * Mirrors Wrangle `10.png` / `15.png` / `20.png` / `25.png`.
 *
 * The three sub-routes are:
 *   /onboarding/start      → Step 1 — personal details
 *   /onboarding/workspace  → Step 2 — workspace setup
 *   /onboarding/team       → Step 3 — invite teammates
 */

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { GGJLogoFull } from '@shared/components/Brand/GGJLogo';

/* ── progress item definition ── */
interface ChecklistItem {
  id: string;
  label: string;
  sub: string;
  route: string;
}

const CHECKLIST: ChecklistItem[] = [
  { id: 'start',     label: 'Google Integration', sub: 'Gmail & Calendar',  route: '/onboarding/start'     },
  { id: 'workspace', label: 'Workspace Setup',    sub: '',                  route: '/onboarding/workspace' },
  { id: 'team',      label: 'Invite Teammates',   sub: '',                  route: '/onboarding/team'      },
];

const ROUTE_TO_IDX: Record<string, number> = {
  '/onboarding/start':     0,
  '/onboarding/workspace': 1,
  '/onboarding/team':      2,
};

function stepIndexFromPath(path: string): number {
  if (path.includes('/workspace')) return 1;
  if (path.includes('/team'))      return 2;
  return 0;
}

/* ── Progress widget (§14 B) ── */
function ProgressWidget({ completedUpTo }: { completedUpTo: number }) {
  const { user } = useAuth();
  const done = completedUpTo;
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const initials = (() => {
    const n = user?.name ?? '';
    const parts = n.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'U';
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })();

  return (
    <aside className="w-[260px] shrink-0 flex flex-col">
      <div className="rounded-[14px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] overflow-hidden flex flex-col">

        {/* Avatar + name header */}
        <div className="px-5 pt-5 pb-4 border-b border-[var(--vscode-border-primary)]">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold"
              style={{ background: 'color-mix(in srgb, #8B80F9 18%, var(--vscode-bg-tertiary))', color: '#8B80F9' }}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold vscode-text-primary truncate leading-tight">
                {user?.name ?? 'Your name'}
              </p>
              <p className="text-[11px] vscode-text-tertiary truncate leading-tight">
                {user?.orgName ?? 'Your workspace'}
              </p>
            </div>
          </div>

          {/* N/3 emerald progress chip */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">Onboarding Status</span>
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-3 h-3" />
              {done}/{CHECKLIST.length}
            </span>
          </div>
        </div>

        {/* Checklist rows */}
        <div className="flex-1 px-3 py-3 space-y-1">
          {CHECKLIST.map((item, idx) => {
            const isDone = idx < completedUpTo;
            const isActive = idx === completedUpTo;
            return (
              <div
                key={item.id}
                className={[
                  'flex items-start gap-3 rounded-[10px] px-3 py-2.5',
                  isActive ? 'bg-[color-mix(in_srgb,#8B80F9_8%,transparent)]' : 'hover:vscode-bg-hover',
                ].join(' ')}
              >
                {isDone ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
                ) : (
                  <Circle className={`w-4 h-4 mt-0.5 shrink-0 ${isActive ? 'text-[#8B80F9]' : 'vscode-text-tertiary'}`} />
                )}
                <div className="min-w-0">
                  <p className={`text-[12px] font-semibold leading-tight ${isDone ? 'line-through vscode-text-tertiary' : 'vscode-text-primary'}`}>
                    {item.label}
                  </p>
                  {item.sub && (
                    <p className="text-[11px] vscode-text-tertiary mt-0.5 leading-tight">{item.sub}</p>
                  )}
                  {isDone && (
                    <span className="mt-1 inline-block text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Done</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer: brand mark + date */}
        <div className="border-t border-[var(--vscode-border-primary)] px-4 py-3 flex items-center gap-2">
          <GGJLogoFull iconSize={22} fontSize={11} />
          <span className="ml-auto text-[10px] vscode-text-tertiary">{today}</span>
        </div>
      </div>
    </aside>
  );
}

/* ── Main layout ── */
export function OnboardingLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const stepIdx = stepIndexFromPath(pathname);

  // Provide navigate + ROUTE_TO_IDX to children via outlet context
  const ctx: OnboardingOutletContext = {
    stepIdx,
    navigate,
    totalSteps: CHECKLIST.length,
  };

  return (
    /* dimmed full-screen backdrop */
    <div className="min-h-screen bg-[color-mix(in_srgb,var(--vscode-bg-primary)_96%,#000)] flex items-center justify-center p-4 sm:p-8">

      {/* 1000px centred container */}
      <div className="w-full max-w-[1000px] flex items-start gap-8">

        {/* Left: 420px form card */}
        <div className="w-[420px] shrink-0 rounded-[14px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] overflow-hidden">
          <Outlet context={ctx} />
        </div>

        {/* Right: 260px progress widget */}
        <ProgressWidget completedUpTo={stepIdx} />

      </div>
    </div>
  );
}

/** Context type exposed to child step routes via `useOutletContext`. */
export interface OnboardingOutletContext {
  stepIdx: number;
  navigate: ReturnType<typeof useNavigate>;
  totalSteps: number;
}

export { ROUTE_TO_IDX };
